// src/websocket/socket.ts
import EventEmitter from 'eventemitter3';
import { Auth } from '../auth/auth';
import { Constant } from '../config/constant';
import { Subscription } from './subscription';
import { Interception } from './interception';
import { LongPollClient } from './http_poll';
import { get_channel_config } from './helper_functions';
import { LiveObjSubscription } from './live_object_subscription';
export default class Socket extends EventEmitter {
    constructor(encrypt, decrypt, lpClient) {
        super();
        this.subscriptions = new Map();
        this.interceptors = new Map();
        this.connection = null;
        this.isConnectionActive = false;
        this.pendingSendMessges = [];
        this.pendingIncomingMessages = new Map();
        this.pullSource = 'socket';
        this.pushSource = 'socket';
        this.isConnecting = false;
        this.autoReconnect = false;
        this.handleConnectionBinding = (data) => {
            this.connection = {
                ConnectionId: data["connection_id"],
                InstanceId: data["instance_id"],
                TenantName: this.credentials["OrgTitle"],
                Environment: this.credentials["Environment"],
                ProjectKey: this.credentials["ProjectKey"],
            };
            console.log('Live connection opened', this.connection.ConnectionId);
            this.isConnectionActive = true;
            this.emit('connection', this.connection);
            if (this.autoReconnect) {
                this.subscriptions.forEach((subscriptionInstance, subscription) => {
                    subscriptionInstance.reconnect();
                });
                this.interceptors.forEach((interceptorInstance, interceptor) => {
                    interceptorInstance.reconnect();
                });
            }
        };
        this.encrypt = encrypt;
        this.decrypt = decrypt;
        this.lpClient = lpClient;
    }
    static getInstance(encrypt, decrypt) {
        if (!Socket.instance) {
            const lpClient = new LongPollClient({
                endpoint: Constant.LPOLL,
                getAuthHeaders: async () => {
                    const auth = Auth.getInstance();
                    await auth.authenticate();
                    const { AccessToken } = auth.getAuthData();
                    const { OrgTitle, Environment, ProjectKey } = auth.getCredentials();
                    return {
                        'Authorization': `Bearer ${AccessToken}`,
                        'X-Org': OrgTitle,
                        'Environment': Environment,
                        'ProjectKey': ProjectKey,
                    };
                },
                onMessages: msgs => { Socket.instance.processIncomingMessages(msgs); },
                onError: err => console.error('LP error:', err),
            });
            Socket.instance = new Socket(encrypt, decrypt, lpClient);
        }
        return Socket.instance;
    }
    async initiateSocket(credentials) {
        var _a;
        this.credentials = credentials;
        if (typeof WebSocket !== 'undefined') {
            try {
                await this.connectWebSocket();
                this.pullSource = 'socket';
                this.pushSource = 'socket';
                return;
            }
            catch (wsErr) {
                console.warn('WebSocket failed, falling back to SSE:', wsErr);
            }
        }
        if (typeof EventSource !== 'undefined') {
            try {
                await this.connectSSE();
                this.pullSource = 'sse';
                this.pushSource = 'http'; // SSE is pull only
                return;
            }
            catch (sseErr) {
                console.warn('SSE failed, falling back to HTTP poll:', sseErr);
            }
        }
        this.pullSource = 'http';
        this.pushSource = 'http';
        this.lpClient.start((_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId);
    }
    async connectWebSocket() {
        var _a, _b;
        try {
            if (this.isConnecting)
                return;
            this.isConnecting = true;
            // Get the Auth singleton instance
            const auth = Auth.getInstance(this.credentials);
            let authData;
            try {
                authData = await auth.authenticate(true);
            }
            catch (err) {
                console.error("Authentication failed:", err);
                this.isConnecting = false;
                this.emit('close', { type: "error" });
                return; // don't proceed if auth fails
            }
            const params = new URLSearchParams({
                connection_id: (_b = (_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId) !== null && _b !== void 0 ? _b : "",
                "Org-Title": this.credentials.OrgTitle,
                token: authData.AccessToken,
                environment: this.credentials.Environment,
                "project-key": this.credentials.ProjectKey,
            });
            // Note: URLSearchParams will handle encoding for you
            const wsUrl = new URL(Constant.WS_URL);
            wsUrl.search = params.toString();
            // And finally:
            const fullWsUrl = wsUrl.toString();
            await this.safeClose();
            await new Promise((resolve, reject) => {
                const ws = new WebSocket(fullWsUrl);
                let timedOut = false;
                const timer = setTimeout(() => {
                    timedOut = true;
                    ws.close();
                    reject(new Error('WebSocket handshake timeout'));
                }, 5000);
                // Setup event handlers
                ws.onopen = (event) => {
                    clearTimeout(timer);
                    if (timedOut)
                        return;
                    this.isConnecting = false;
                    this.isConnectionActive = true;
                    this.emit('open', event);
                    this.websocket = ws;
                    this.listenConfiguration();
                    resolve();
                };
                ws.onmessage = (event) => {
                    this.parseIncomingMessage(event.data);
                };
                ws.onerror = (event) => {
                    if (timedOut)
                        return;
                    clearTimeout(timer);
                    console.error('Live error:', JSON.stringify(event));
                    // this.emit('error', event);
                    this.isConnecting = false;
                    this.isConnectionActive = false;
                    this.emit('close', { type: "error" });
                    reject(new Error('WebSocket connection error'));
                };
                ws.onclose = (event) => {
                    if (timedOut)
                        return;
                    clearTimeout(timer);
                    console.log('Live closed, code=', event.code, 'reason=', event.reason);
                    this.isConnecting = false;
                    this.isConnectionActive = false;
                    this.emit('close', event);
                    reject(new Error(`WebSocket closed with code ${event.code}`));
                };
            }).catch(err => {
                this.isConnecting = false;
                this.isConnectionActive = false;
                this.emit('close', { type: 'error', message: err.message });
                throw err; // so your fallbacks kick in
            });
        }
        catch (e) {
            console.log("No server present");
            this.isConnecting = false;
            this.isConnectionActive = false;
            this.emit('close', { type: "error" });
            return;
        }
    }
    async safeClose(timeout = 1000) {
        const ws = this.websocket;
        if (!ws)
            return;
        if (ws.readyState === WebSocket.CLOSED)
            return;
        await new Promise(resolve => {
            const onClose = () => {
                ws.removeEventListener('close', onClose);
                clearTimeout(timer);
                resolve();
            };
            const timer = setTimeout(() => {
                ws.removeEventListener('close', onClose);
                resolve();
            }, timeout);
            ws.addEventListener('close', onClose);
            try {
                ws.close();
            }
            catch (_a) { }
        });
    }
    async connectSSE() {
        // Get the Auth singleton instance
        const auth = Auth.getInstance(this.credentials);
        let authData;
        try {
            authData = await auth.authenticate();
        }
        catch (err) {
            console.error("Authentication failed:", err);
            this.emit('close', { type: "error" });
            return; // don't proceed if auth fails
        }
        // 1) Build a plain object where every value is a string
        const params = {
            "Org-Title": encodeURIComponent(this.credentials.OrgTitle),
            token: authData.AccessToken,
            environment: encodeURIComponent(this.credentials.Environment),
            "project-key": encodeURIComponent(this.credentials.ProjectKey),
        };
        const qs = new URLSearchParams(params).toString();
        const url = `${Constant.SSE_URL}?${qs}`;
        const es = new EventSource(url);
        es.onopen = () => {
            console.log("SSE fallback opened");
            this.emit("open", new Event("open"));
            this.isConnectionActive = true;
            this.listenConfiguration();
        };
        es.onerror = (err) => {
            console.error("SSE error:", err);
            this.emit("error", err);
        };
        es.addEventListener("close", (e) => {
            console.log("SSE server requested close, shutting down client EventSource.");
            this.isConnectionActive = false;
            es.close();
            // optionally, set a flag to prevent reconnect logic if you have any
        });
        es.onmessage = (e) => {
            // e.data is the raw JSON or array payload
            this.parseIncomingMessage(e.data);
        };
    }
    async listenConfiguration() {
        this.setAutoReconnect(true);
        const config = await this.subscribe("art_config");
        this.art_secure_line = await this.subscribe('art_secure');
        try {
            config.remove("connection", this.handleConnectionBinding);
            config.bind("connection", this.handleConnectionBinding);
            this.art_secure_line.push("connection-config", {});
        }
        catch (error) {
            console.error("Error in listening configuration:", error);
        }
    }
    async pushForSecureLine(event, data) {
        this.art_secure_line.push(event, data, {});
    }
    async listenForSecureLine(event, validationFn) {
        if (!this.art_secure_line) {
            throw new Error('Secure channel not initialized.');
        }
        // Return a promise that resolves when ecr_key data is received
        return new Promise((resolve, reject) => {
            const onEcrKey = (data) => {
                // Resolve the promise with the received data
                let valid = true;
                if (validationFn) {
                    valid = validationFn === null || validationFn === void 0 ? void 0 : validationFn(data);
                }
                if (valid) {
                    resolve(data);
                    // Remove the listener after resolving
                    this.art_secure_line.off(event, onEcrKey);
                }
            };
            // Add the listener for ecr_key
            this.art_secure_line.on(event, onEcrKey);
            // Optionally, add a timeout to reject if no response is received
            setTimeout(() => {
                // Reject the promise with an error
                reject(new Error(`${event} response timeout`));
                // Ensure the listener is also removed on timeout
                this.art_secure_line.off(event, onEcrKey);
            }, 20000);
        });
    }
    /**
       * Subscribe to a specific channel.
       * @param channel The channel name to subscribe to.
       * @returns A Subscription instance for the specified channel.
       */
    async subscribe(channel) {
        return this.handleSubscription(channel, "subscribe");
    }
    /**
       * Subscribe to listen presence list of specific channel.
       * @param channel The channel name to subscribe to.
       * @returns A Presence instance for the specified channel.
       */
    async presence(channel) {
        return this.handleSubscription(channel, "presence");
    }
    async handleSubscription(channel, process) {
        await this.wait();
        // Check if a subscription for the channel already exists
        if (this.subscriptions.has(channel)) {
            const sub = this.subscriptions.get(channel);
            if (process === "subscribe") {
                sub.subscribe();
            }
            else if (process === "presence") {
                sub.presence();
            }
            return sub;
        }
        const channelConfig = await this.validateSubscription(channel, process);
        if (!channelConfig) {
            return Promise.reject(new Error(`Channel ${channel} not found`));
        }
        let subscription;
        if (channelConfig.channelType === "shared-object") {
            subscription = new LiveObjSubscription(channelConfig, this, process);
        }
        else {
            subscription = new Subscription(channelConfig, this, process);
        }
        this.subscriptions.set(channel, subscription);
        const buf = this.pendingIncomingMessages.get(channel);
        if (buf) {
            for (const { event, payload } of buf) {
                subscription.handleMessage(event, payload);
            }
            this.pendingIncomingMessages.delete(channel);
        }
        return subscription;
    }
    async validateSubscription(channelName, process) {
        return new Promise(async (resolve, reject) => {
            if (["art_config", "art_secure"].includes(channelName)) {
                return resolve({
                    channelName: channelName,
                    channelType: "default",
                    snapshot: null,
                });
            }
            await get_channel_config(channelName, process, this).then((config) => {
                resolve(config);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    getConnection() {
        return this.connection;
    }
    /**
       * Register interceptor.
       * @param interceptor The interceptor name to register to.
       * @returns A Interception instance.
       */
    async intercept(interceptor, fn) {
        return new Promise(async (resolve, reject) => {
            await this.wait();
            if (this.interceptors.has(interceptor)) {
                resolve(this.interceptors.get(interceptor));
                return;
            }
            // Create a new Subscription instance
            const interception = new Interception(interceptor, fn, this);
            await interception.validateInterception().then((config) => {
                this.interceptors.set(interceptor, interception);
                resolve(interception);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    parseIncomingMessage(message) {
        try {
            const parsed = JSON.parse(message);
            if (Array.isArray(parsed)) {
                this.processIncomingMessages(parsed);
                return;
            }
            else {
                this.handleIncomingMessage(parsed);
            }
        }
        catch (error) {
            console.error('Failed to parse incoming message:', error);
        }
    }
    processIncomingMessages(messages) {
        messages.forEach(msg => this.handleIncomingMessage(msg));
    }
    /**
     * Handle incoming WebSocket messages and route them to the appropriate Subscription.
     * @param message The raw message received from the WebSocket.
     */
    handleIncomingMessage(parsedMessage) {
        try {
            const { channel, namespace, event, interceptor_name, content: data } = parsedMessage;
            if (!channel || !event) {
                console.warn('Received message without channel or event:', parsedMessage);
                return;
            }
            if (event === 'shift_to_http') {
                // switch over to HTTP polling
                this.switchToHttpPoll();
                return;
            }
            if (event === 'error') {
                console.error('Received error message:', parsedMessage);
                // return
            }
            if (interceptor_name) {
                const interception = this.interceptors.get(interceptor_name);
                if (interception) {
                    delete parsedMessage["content"];
                    parsedMessage['data'] = data;
                    interception.handleMessage(channel, parsedMessage);
                }
                else {
                    console.warn(`No Interception found for channel: ${channel}`);
                }
            }
            else {
                let subscriptionKey = channel;
                if (namespace) {
                    subscriptionKey += ":" + namespace;
                }
                const subscription = this.subscriptions.get(subscriptionKey);
                delete parsedMessage["content"];
                parsedMessage['data'] = data;
                if (subscription) {
                    if (subscription instanceof Subscription || subscription instanceof LiveObjSubscription) {
                        subscription.handleMessage(event, parsedMessage);
                    }
                }
                else {
                    console.warn(`No subscription found for channel: ${subscriptionKey}, adding to buffer`);
                    const arr = this.pendingIncomingMessages.get(subscriptionKey) || [];
                    arr.push({ event, payload: parsedMessage });
                    this.pendingIncomingMessages.set(subscriptionKey, arr);
                }
            }
        }
        catch (error) {
            console.error('Failed to parse incoming message:', error);
        }
    }
    switchToHttpPoll() {
        var _a, _b;
        if (this.pullSource === 'http')
            return; // already polling
        console.info('🔄 Shifting pullSource → http, pushSource → http');
        this.pullSource = 'http';
        this.pushSource = 'http';
        // start long-poller (it will reuse connection_id you already have)
        this.lpClient.start((_b = (_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId) !== null && _b !== void 0 ? _b : "");
    }
    /**
     * Send a message through the WebSocket.
     * @param message The message to send.
     */
    sendMessage(message) {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(message);
            return true;
        }
        else {
            this.pendingSendMessges.push(message);
            const error = new Error('WebSocket is not open. Cannot send message.');
            console.warn(error, message);
            this.emit('error', error);
            return false;
        }
    }
    setAutoReconnect(autoReconnect) {
        this.autoReconnect = autoReconnect;
    }
    closeWebSocket() {
        if (this.websocket) {
            this.isConnectionActive = false;
            this.connection = null;
            this.isConnecting = false;
            this.websocket.close();
        }
    }
    wait() {
        if (this.isConnectionActive)
            return Promise.resolve();
        return new Promise(resolve => {
            const onConn = () => {
                this.off('connection', onConn);
                resolve();
            };
            this.on('connection', onConn);
        });
    }
}
